import { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { Toast as ToastData } from '../contexts/ToastContext';
import { useToastContext } from '../contexts/ToastContext';

interface ToastProps {
  toast: ToastData;
}

export const Toast = ({ toast }: ToastProps) => {
  const { removeToast } = useToastContext();
  const [show, setShow] = useState(true);

  // Auto-hide after duration
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        setShow(false);
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleClose = () => {
    setShow(false);
  };

  const handleAfterLeave = () => {
    removeToast(toast.id);
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon aria-hidden="true" className="size-6 text-green-400" />;
      case 'error':
        return <XCircleIcon aria-hidden="true" className="size-6 text-red-400" />;
      case 'warning':
        return <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-yellow-400" />;
      default:
        return <InformationCircleIcon aria-hidden="true" className="size-6 text-blue-400" />;
    }
  };

  return (
    <Transition
      show={show}
      enter="transform transition duration-300 ease-out"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition duration-100 ease-in"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      afterLeave={handleAfterLeave}
    >
      <div
        role={toast.type === 'error' ? 'alert' : 'status'}
        className="pointer-events-auto w-full max-w-sm rounded-lg bg-white shadow-lg outline-1 outline-black/5 transition dark:bg-gray-800 dark:-outline-offset-1 dark:outline-white/10"
      >
        <div className="p-4">
          <div className="flex items-start">
            <div className="shrink-0">
              {getIcon()}
            </div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {toast.message}
              </p>
              {toast.description && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {toast.description}
                </p>
              )}
            </div>
            <div className="ml-4 flex shrink-0">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600 dark:hover:text-white dark:focus:outline-indigo-500"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

